import React, { useEffect, useState } from 'react'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
// MUI Imports
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import PolicyDescriptionPopup from './PolicyDescriptionPopup'
import PolicyTemplateDrawer from './PolicyTemplateDrawer'
import { Stack, Switch } from '@mui/material'

const processSteps = [
  {
    title: '1. Create a template here',
    detail:
      'Add each policy as a template with its title and category. Templates are the containers the actual documents live in.',
  },
  {
    title: '2. Upload the document',
    detail:
      'Each template appears in the Policy Library. Open its policy page to upload the PDF as version 1, and upload new versions as the policy changes.',
  },
  {
    title: '3. Staff acknowledge',
    detail:
      'Staff are prompted to read and acknowledge the latest version, and acknowledgement compliance is tracked automatically.',
  },
]

const Policies = () => {
  const [loading, setLoading] = useState(true)
  const [policies, setPolicies] = useState([])
  const [fetchError, setFetchError] = useState()
  const [addNew, setAddNew] = useState(false)
  const [selectedPolicyId, setSelectedPolicyId] = useState(null)
  const [includeArchived, setIncludeArchived] = useState(false)

  const getPolicies = () => {
    setLoading(true)
    API.get(`/policies/descriptions?includearchived=${includeArchived}`)
      .then((res) => setPolicies(res.data))
      .catch((e) => setFetchError(e))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    getPolicies()
  }, [includeArchived])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {loading && <LinearProgress />}
      <Box display="flex" flexDirection="column">
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
        >
          <Stack>
            <Typography variant="title2">Policies Set-Up</Typography>
            <Typography variant="caption">
              Use this area to set up the policies throughout the organisation.
            </Typography>
          </Stack>
          <Stack alignItems="flex-end" gap={1}>
            <Box>
              <Button
                onClick={() => setAddNew(true)}
                variant="contained"
                color="anMainBlue"
                size="small"
              >
                Add New
              </Button>
            </Box>
            <Stack alignItems="center" direction="row">
              <Switch
                checked={includeArchived}
                size="small"
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              <Typography fontWeight={900} variant="body2">
                Include Archived
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(20,108,148,0.04)',
        }}
      >
        <Typography fontWeight={800} gutterBottom>
          How policies work
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {processSteps.map((step) => (
            <Box key={step.title} flex={1}>
              <Typography variant="body2" fontWeight={700}>
                {step.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {step.detail}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
      <CustomDataGrid
        rows={policies}
        columns={[
          { field: 'category', headerName: 'Category', flex: 0.7 },
          {
            field: 'policyTitle',
            headerName: 'Policy Title',
            flex: 1,
            renderCell: ({ row }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{row.policyTitle}</Typography>
                {row.archived && (
                  <Chip label="Archived" size="small" color="default" />
                )}
              </Stack>
            ),
          },
          {
            field: 'policies',
            headerName: 'Versions',
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value) => value?.length ?? 0,
            renderCell: ({ value }) =>
              value > 0 ? (
                value
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Not uploaded
                </Typography>
              ),
          },
        ]}
        getRowId={(params) => params._id}
        disableRowSelectionOnClick
        slots={{ footer: () => null, toolbar: () => null }}
        onRowClick={({ row }) => setSelectedPolicyId(row._id)}
      />
      <PolicyDescriptionPopup
        open={addNew}
        onClose={() => setAddNew(false)}
        policies={policies}
        getPolicies={getPolicies}
      />
      <PolicyTemplateDrawer
        open={Boolean(selectedPolicyId)}
        policyId={selectedPolicyId}
        policies={policies}
        onClose={() => setSelectedPolicyId(null)}
        onChanged={getPolicies}
      />
    </Box>
  )
}

export default Policies
